import { CaptureScreen } from '../design-system/shell/capture-screen';
import { useState, useCallback } from "react";
import { CameraOverlay } from "../components/scan/CameraOverlay";
import { useParams } from "react-router";
import { Permissions } from "../components/scan/Permissions";
import { PIPEDB_URL } from "../lib/pipedb-url";
import { useAuth } from "../context/AuthContext";

// Both modes go through the API's /debug/upload endpoint; `target` picks which
// rembg instance it forwards to. No rembg credentials ever reach the client.
// "local" only resolves when the Functions host runs on this machine.
type Backend = "local" | "auto";

interface RembgResult {
  status: string;
  reason?: string;
  iou?: number;
  ssim?: number;
  keypoint_ratio?: number;
  keypoints?: number[][];
  pose_valid?: boolean;
  pose_issues?: string[];
  pixels?: string;
  backend?: string;
}

const CamTest: React.FC = () => {
  const pose = useParams<{ pose: string }>().pose;
  const { getAccessTokenSilently } = useAuth();
  const [permission, setPermission] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState<RembgResult | null>(null);
  const [maskSrc, setMaskSrc] = useState<string | null>(null);
  const [capturedSrc, setCapturedSrc] = useState<string | null>(null);
  const [backend, setBackend] = useState<Backend>("local");

  const sendToRembg = useCallback(async (dataUrl: string) => {
    setValidating(true);
    setResult(null);
    setMaskSrc(null);
    setCapturedSrc(dataUrl);

    try {
      const resp = await fetch(dataUrl);
      const blob = await resp.blob();
      const ext = blob.type === "image/png" ? "png" : "jpg";

      const form = new FormData();
      form.append("image", blob, `capture.${ext}`);
      form.append("gender", "male");

      const viewName = pose || "front";

      const token = await getAccessTokenSilently();
      const res = await fetch(
        `${PIPEDB_URL}/debug/upload/${viewName}?target=${backend}`,
        {
          method: "POST",
          body: form,
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        },
      );

      let json: RembgResult;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("json")) {
        const body = await res.json();
        json = typeof body === "string" ? JSON.parse(body) : body;
      } else {
        json = { status: "error", reason: `${res.status} ${await res.text()}` };
      }

      json.backend = backend;
      setResult(json);

      if (json.pixels) {
        setMaskSrc(`data:image/webp;base64,${json.pixels}`);
      }
    } catch (e: any) {
      setResult({ status: "error", reason: e.message, backend });
    } finally {
      setValidating(false);
    }
  }, [backend, pose, getAccessTokenSilently]);

  const clearResult = () => {
    setResult(null);
    setMaskSrc(null);
    setCapturedSrc(null);
  };

  const metricColor = (label: string, val: number | undefined) => {
    if (val === undefined) return "#94a3b8";
    if (label === "IoU") return val >= 0.875 ? "#4ade80" : val >= 0.7 ? "#fbbf24" : "#f87171";
    if (label === "SSIM") return val >= 0.9 ? "#4ade80" : val >= 0.7 ? "#fbbf24" : "#f87171";
    if (label === "Keypoint ratio") return val >= 0.6 ? "#4ade80" : "#f87171";
    return "#e2e8f0";
  };

  return (
    <CaptureScreen className="z-50">
        <Permissions updated={(granted: any) => setPermission(granted.gyro)} />

        {/* Backend selector — always visible at top */}
        <div style={{
          position: "fixed", top: 8, right: 8, zIndex: 10000,
          display: "flex", gap: 4, background: "rgba(0,0,0,0.65)",
          borderRadius: 8, padding: 4, backdropFilter: "blur(8px)",
        }}>
          {(["local", "auto"] as Backend[]).map(b => (
            <button
              key={b}
              onClick={() => setBackend(b)}
              style={{
                padding: "5px 12px", borderRadius: 6, border: "none",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                background: backend === b ? "#3b82f6" : "transparent",
                color: backend === b ? "#fff" : "#94a3b8",
              }}
            >
              {b === "local" ? "Local rembg" : "Hosted rembg"}
            </button>
          ))}
        </div>

        {/* Pose label */}
        <div style={{
          position: "fixed", top: 8, left: 8, zIndex: 10000,
          background: "rgba(0,0,0,0.65)", borderRadius: 8, padding: "5px 12px",
          color: "#fff", fontSize: 13, fontWeight: 600, backdropFilter: "blur(8px)",
        }}>
          pose: {pose || "front"}
        </div>

        {/* Result overlay */}
        {(result || validating) && (
          <div
            style={{
              position: "fixed", inset: 0, zIndex: 9999,
              background: "rgba(0,0,0,0.88)", color: "#fff",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              padding: 20, gap: 14, overflowY: "auto",
            }}
          >
            {validating && (
              <div style={{ fontSize: 18 }}>
                ⏳ Sending to {backend === "local" ? "local rembg" : "live API"}...
              </div>
            )}

            {result && (
              <>
                <div style={{
                  fontSize: 28, fontWeight: 700,
                  color: result.status === "done" ? "#4ade80"
                    : result.status === "rejected" ? "#f87171" : "#fbbf24",
                }}>
                  {result.status === "done" ? "✅ Accepted"
                    : result.status === "rejected" ? "❌ Rejected"
                    : `⚠️ ${result.status}`}
                </div>

                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {PIPEDB_URL}/debug/upload → rembg [{result.backend}]
                </div>

                {result.reason && (
                  <div style={{
                    background: "rgba(248,113,113,0.15)", border: "1px solid #f87171",
                    borderRadius: 8, padding: "10px 16px", maxWidth: 500, textAlign: "center",
                    fontSize: 14,
                  }}>
                    {result.reason}
                  </div>
                )}

                {/* Metrics */}
                <table style={{
                  borderCollapse: "collapse", fontSize: 14,
                  fontFamily: "ui-monospace, monospace",
                  background: "rgba(255,255,255,0.06)",
                  borderRadius: 8, overflow: "hidden",
                }}>
                  <tbody>
                    {([
                      ["IoU", result.iou],
                      ["SSIM", result.ssim],
                      ["Keypoint ratio", result.keypoint_ratio],
                    ] as [string, number | undefined][]).map(([label, val]) => (
                      <tr key={label}>
                        <td style={{ padding: "6px 14px", color: "#94a3b8", textAlign: "right" }}>{label}</td>
                        <td style={{
                          padding: "6px 14px", fontWeight: 600,
                          color: metricColor(label, val),
                        }}>
                          {val !== undefined ? (val === 1 ? "1.000 (skipped)" : val.toFixed(3)) : "—"}
                        </td>
                      </tr>
                    ))}
                    {result.pose_valid !== undefined && (
                      <tr>
                        <td style={{ padding: "6px 14px", color: "#94a3b8", textAlign: "right" }}>Pose valid</td>
                        <td style={{
                          padding: "6px 14px", fontWeight: 600,
                          color: result.pose_valid ? "#4ade80" : "#f87171",
                        }}>
                          {String(result.pose_valid)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pose issues */}
                {result.pose_issues && result.pose_issues.length > 0 && (
                  <div style={{
                    background: "rgba(251,191,36,0.12)", border: "1px solid #fbbf24",
                    borderRadius: 8, padding: "10px 16px", maxWidth: 500, fontSize: 13,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Pose issues:</div>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {result.pose_issues.map((issue, i) => (
                        <li key={i}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Images */}
                <div style={{
                  display: "flex", gap: 12, maxWidth: "90vw",
                  flexWrap: "wrap", justifyContent: "center",
                }}>
                  {capturedSrc && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>INPUT</div>
                      <img src={capturedSrc} style={{ maxHeight: 220, borderRadius: 6 }} />
                    </div>
                  )}
                  {maskSrc && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>MASK</div>
                      <img src={maskSrc} style={{ maxHeight: 220, borderRadius: 6 }} />
                    </div>
                  )}
                </div>

                <button
                  onClick={clearResult}
                  style={{
                    marginTop: 8, padding: "10px 28px", borderRadius: 8,
                    border: "1px solid #475569", background: "transparent",
                    color: "#fff", fontSize: 15, cursor: "pointer",
                  }}
                >
                  ← Back to camera
                </button>
              </>
            )}
          </div>
        )}

        {permission && (
          <CameraOverlay
            setUploaded={() => {}}
            sessionID={"debug-session"}
            profileID={"debug-profile"}
            pose={(pose || "front") as "front" | "right"}
            gender={"male"}
            uploadPhoto={(data: string) => {
              sendToRembg(data);
            }}
          />
        )}
    </CaptureScreen>
  );
};

export default CamTest;
